import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import type { SubmittedInvoice } from '~/api/invoices/types';

import { invoiceHistoryQueryOptions, useInvoiceHistory } from '~/api/invoices';
import { Card, InfoBlock, SkeletonText, Timeline } from '~/components';

import { InvoiceHistoryItem } from './InvoiceHistoryItem';
import { cardStyle, skeletonStyle } from './InvoiceTimeline.css';
import { historyRecordIcons, upcomingMilestones } from './InvoiceTimeline.utils';

type Props = {
  invoice: SubmittedInvoice;
};

export const InvoiceTimeline = ({ invoice }: Props) => {
  const { data: history, isError, isPending } = useInvoiceHistory(invoice.id);
  const queryClient = useQueryClient();

  const { t } = useTranslation(['invoices']);

  if (isPending) {
    return (
      <Card.Root className={cardStyle} size="small">
        <InfoBlock label={t('invoices:details.timeline.title')}>
          <div className={skeletonStyle}>
            {Array.from({ length: 4 }, (_, index) => (
              <SkeletonText fontSize="small" key={index} lineHeight="normal" width="70%" />
            ))}
          </div>
        </InfoBlock>
      </Card.Root>
    );
  }

  if (isError) {
    return (
      <Card.Root className={cardStyle} size="small">
        <InfoBlock label={t('invoices:details.timeline.title')}>
          <Card.Error
            onRetry={() => queryClient.resetQueries({ queryKey: invoiceHistoryQueryOptions(invoice.id).queryKey })}
            title={t('invoices:details.history.error.title')}
          />
        </InfoBlock>
      </Card.Root>
    );
  }

  const recordedTypes = new Set(history.map(({ type }) => type));

  return (
    <Card.Root className={cardStyle} size="small">
      <InfoBlock label={t('invoices:details.timeline.title')}>
        <Timeline.Root>
          {history.map((record, index) => (
            <InvoiceHistoryItem key={index} record={record} />
          ))}

          {invoice.status !== 'Credited' &&
            upcomingMilestones
              .filter((type) => !recordedTypes.has(type))
              .map((type) => (
                <Timeline.Item icon={historyRecordIcons[type]} key={type}>
                  <Trans i18nKey={`invoices:details.history.message.${type}.default`} />
                </Timeline.Item>
              ))}
        </Timeline.Root>
      </InfoBlock>
    </Card.Root>
  );
};
